import React, { useMemo } from 'react';

import { TextInput } from '@components/admin/Forms';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogTitle,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogActions,
  ContainerProps,
} from '@components/Dialog';
import MUIGrid from '@material-ui/core/Grid';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, requiredStringField, stringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { Formik } from 'formik';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export interface CancelBookingInquiryDialogProps extends ContainerProps {
  inquiry: Partial<{
    number: string | null;
    transmissionTime: string | null;
    author: string | null;
    status: string | null;
  }>;
  onSubmit(values: Values): void;
}

const strings = createStrings({
  ja: {
    number: '番号',
    transmissionTime: '送信時間',
    author: '作成者',
    status: 'ステータス',
    title: '予約をキャンセル',
    inquiringMessage: '下記のお問い合わせが取消されますが、進めてよろしいでしょうか。',
    reason: 'キャンセル理由',
    reasonType: 'キャンセル理由',
    cancelledReason: 'キャンセル理由',
    noMoreImportCases: '輸入案件がなくなりました',
    importCaseHasBeenPostponed: '輸入案件は延期されました',
    projectHasBeenSentToAnotherCompany: '他社へ依頼しました',
    other: 'その他',
    cancel: '取消',
    execution: '実行',
    pleaseSelectOne: '項目を選択してください',
    pleaseTellMeWhyYouCanceled: 'キャンセル理由を教えて下さい',
  },
});

const valuesSchema = t.type({
  reasonType: requiredStringField(strings.reasonType),
  cancelledReason: h.omittable(stringField(strings.cancelledReason)),
});

export type Values = t.TypeOf<typeof valuesSchema>;

const validate = createValidate(valuesSchema);

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.middleText,
    minHeight: 410,
  },
  inputContainer: {
    position: 'relative',
    height: 43,
    ...common.verticalSpace1,
  },
  optionText: {
    ...common.middleText,
  },
  bookingInquiryData: {
    fontWeight: 'bold',
    [theme.breakpoints.down('xs')]: {
      marginLeft: '1rem',
    },
  },
}));

const Inquiry: React.FC<Pick<CancelBookingInquiryDialogProps, 'inquiry'>> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { inquiry } = props;

  return (
    <>
      <MUIGrid container>
        <MUIGrid item xs={12} sm={4}>
          {strings.number}
        </MUIGrid>
        <MUIGrid item xs={12} sm={8}>
          <span className={classes.bookingInquiryData}>{inquiry.number}</span>
        </MUIGrid>
      </MUIGrid>
      <MUIGrid container>
        <MUIGrid item xs={12} sm={4}>
          {strings.transmissionTime}
        </MUIGrid>
        <MUIGrid item xs={12} sm={8}>
          <span className={classes.bookingInquiryData}>{inquiry.transmissionTime}</span>
        </MUIGrid>
      </MUIGrid>
      <MUIGrid container>
        <MUIGrid item xs={12} sm={4}>
          {strings.author}
        </MUIGrid>
        <MUIGrid item xs={12} sm={8}>
          <span className={classes.bookingInquiryData}>{inquiry.author}</span>
        </MUIGrid>
      </MUIGrid>
      <MUIGrid container>
        <MUIGrid item xs={12} sm={4}>
          {strings.status}
        </MUIGrid>
        <MUIGrid item xs={12} sm={8}>
          <span className={classes.bookingInquiryData}>{inquiry.status}</span>
        </MUIGrid>
      </MUIGrid>
    </>
  );
};

export const CancelBookingInquiryDialog: React.FC<CancelBookingInquiryDialogProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { inquiry, onSubmit, ...rest } = props;
  const initialValues = useMemo(() => {
    return {
      reasonType: '',
      cancelledReason: undefined,
    };
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={(values) => {
        onSubmit(values);
        rest.onClose?.();
      }}
    >
      {({ handleSubmit, values, resetForm }) => {
        return (
          <form onSubmit={handleSubmit}>
            <ActionableDialog {...rest}>
              <ActionableDialogTitle>{strings.title}</ActionableDialogTitle>
              <ActionableDialogContent>
                <div className={classes.container}>
                  <p>{strings.inquiringMessage}</p>
                  <Inquiry inquiry={inquiry} />

                  <div style={{ height: 16 }} />
                  <ActionableDialogContentDivider />
                  <div style={{ height: 16 }} />

                  <div className={classes.inputContainer}>
                    <TextInput
                      name="reasonType"
                      placeholder={strings.pleaseSelectOne}
                      label={strings.reasonType}
                      itemVariant="column"
                      choose
                      options={[
                        {
                          value: strings.noMoreImportCases,
                          label: strings.noMoreImportCases,
                        },
                        {
                          value: strings.importCaseHasBeenPostponed,
                          label: strings.importCaseHasBeenPostponed,
                        },
                        {
                          value: strings.projectHasBeenSentToAnotherCompany,
                          label: strings.projectHasBeenSentToAnotherCompany,
                        },
                        {
                          value: strings.other,
                          label: strings.other,
                        },
                      ]}
                    />
                    {values.reasonType === strings.other ? (
                      <TextInput
                        placeholder={strings.pleaseTellMeWhyYouCanceled}
                        multiline
                        name="cancelledReason"
                        label=""
                        noLabel
                      />
                    ) : null}
                  </div>
                </div>
              </ActionableDialogContent>
              <ActionableDialogActions>
                <Button
                  color="printable"
                  text={strings.cancel}
                  size="small"
                  onClick={() => {
                    resetForm();
                    rest.onClose?.();
                  }}
                />
                <Button color="primary" text={strings.execution} size="small" type="submit" />
              </ActionableDialogActions>
            </ActionableDialog>
          </form>
        );
      }}
    </Formik>
  );
};
